import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "upstream",
  name: "上流工程",
  nameEn: "Upstream Process",
  seo: {
    title:
      "上流工程を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "要件定義・基本設計・プロジェクト管理・ステークホルダーコミュニケーションまで、上流工程スキルをゆめさくエンジニアで実践的に学びましょう。PM・テックリードを目指す方に最適です。",
    url: "/columns/upstream-programming-school",
  },
  hero: {
    title: "上流工程を学べる女性向けプログラミングスクール",
    englishTitle: "Learn Upstream Process",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "上流工程" },
  ],
  overview: {
    heading: "上流工程とは？",
    paragraphs: [
      "上流工程とは、ソフトウェア開発における要件定義・基本設計・プロジェクト管理など、実際のコーディングに入る前の工程を指します。「何を作るか」「どう作るか」を決める、開発プロジェクトの成否を左右する重要なフェーズです。",
      "クライアントやビジネス部門の要望をヒアリングし、技術的な要件に落とし込む要件定義、システム全体のアーキテクチャを設計する基本設計、プロジェクトのスケジュール・リソース・リスクを管理するプロジェクト管理など、多岐にわたるスキルが求められます。",
      "上流工程のスキルを持つエンジニアは、単なるコーダーではなく「ビジネスを理解して技術で解決する」存在として、高い評価と報酬を得ています。",
    ],
    useCases: [
      {
        label: "要件定義",
        description:
          "クライアントやビジネス部門の要望をヒアリングし、システムに必要な機能・性能・制約を明確に文書化します。",
      },
      {
        label: "基本設計・アーキテクチャ設計",
        description:
          "システム全体の構成、データベース設計、API設計、画面設計など、実装の指針となる設計ドキュメントを作成します。",
      },
      {
        label: "プロジェクト管理",
        description:
          "WBS作成、スケジュール管理、リスク管理、進捗報告など、プロジェクトを計画通りに遂行するためのマネジメントを行います。",
      },
      {
        label: "ステークホルダーコミュニケーション",
        description:
          "技術チーム・ビジネス部門・経営層の間に立ち、技術的な内容を分かりやすく翻訳し、合意形成を図ります。",
      },
    ],
  },
  whyLearn: {
    heading: "上流工程を学ぶべき3つの理由",
    items: [
      {
        title: "エンジニアのキャリアアップに必須のスキル",
        description:
          "コーディングだけのエンジニアと上流工程もできるエンジニアでは、年収に大きな差があります。PM・テックリード・ITコンサルタントなどの高年収ポジションは、上流工程のスキルが必須条件です。",
      },
      {
        title: "プロジェクトの成功率を大幅に向上",
        description:
          "IT プロジェクトの失敗原因の多くは「要件定義の不備」にあります。上流工程を正しく行えるスキルがあれば、プロジェクト全体の品質と成功率を劇的に改善できます。",
      },
      {
        title: "ビジネス経験を技術に活かせる",
        description:
          "営業・企画・マーケティングなどのビジネス経験は、上流工程で大きな強みになります。業務の課題を深く理解し、適切なシステム要件に落とし込む力は、技術力だけでは得られません。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアで上流工程を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、開発スキルを習得した上級Phaseで上流工程のスキルを実践的に学びます。実際のプロジェクトを想定したケーススタディを通じて、要件定義書・設計書の作成、プロジェクト計画の策定を体験します。",
      "技術を知っているからこそできる質の高い要件定義・設計を目指し、コーディングスキルと上流工程スキルの両輪でキャリアアップを実現します。",
    ],
    topics: [
      "要件定義の手法とドキュメント作成",
      "基本設計（画面設計・DB設計・API設計）",
      "UML・ER図によるシステムモデリング",
      "プロジェクト管理の基礎（WBS・ガントチャート）",
      "ステークホルダーマネジメント",
      "リスク管理・品質管理",
      "実践プロジェクト：要件定義から基本設計までの一連のプロセス体験",
    ],
  },
  careerPaths: {
    heading: "上流工程を活かせるキャリア",
    items: [
      {
        title: "PM/プロジェクトマネージャー",
        description:
          "プロジェクト全体の計画・実行・管理を統括する役割です。スケジュール管理、リソース配分、リスク管理、ステークホルダーとの調整など、プロジェクト成功の鍵を握る重要なポジションです。",
        salary: "600〜1,000万円",
      },
      {
        title: "テックリード",
        description:
          "技術面でチームをリードしながら、アーキテクチャ設計や技術選定など上流工程の技術的判断を担います。マネジメントと技術の両方のスキルが求められます。",
        salary: "700〜1,200万円",
      },
      {
        title: "ITコンサルタント",
        description:
          "クライアント企業のIT戦略立案や、大規模システムの要件定義・基本設計を支援する職種です。ビジネス課題を技術で解決する提案力が求められます。",
        salary: "600〜1,100万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "実務経験なしでは習得が非常に困難",
        luagate: "3〜4ヶ月（ケーススタディで実践的に体験）",
      },
      {
        label: "挫折率",
        selfStudy: "書籍の知識だけでは実務に活かせず挫折",
        luagate: "実際のプロジェクトを想定した演習で実践力を養成",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "要件定義・設計を実践する機会がない",
        luagate: "要件定義から設計書作成まで一連のプロセスを体験",
      },
      {
        label: "質問対応",
        selfStudy: "設計の正解がわからず自己流になりがち",
        luagate: "現役PMメンターが設計レビュー・フィードバックを提供",
      },
      {
        label: "就職サポート",
        selfStudy: "未経験でPM職に就くのは極めて困難",
        luagate: "上流工程の実践経験をアピールできるポートフォリオ作成を支援",
      },
    ],
  },
  faq: [
    {
      question: "プログラミング経験がなくても上流工程を学べますか？",
      answer:
        "上流工程を効果的に行うには、技術への基本的な理解が不可欠です。ゆめさくエンジニアではプログラミングの基礎を習得してから上流工程に進むカリキュラムのため、技術を理解した上で質の高い要件定義・設計ができるようになります。",
    },
    {
      question: "上流工程のスキルはフリーランスでも活かせますか？",
      answer:
        "非常に活かせます。上流工程ができるフリーランスエンジニアは、コーディングだけのエンジニアに比べて単価が高く、プロジェクト全体を任されることで継続的な案件獲得にも繋がります。",
    },
    {
      question: "PMとテックリードの違いは何ですか？",
      answer:
        "PMはプロジェクト全体のマネジメント（スケジュール・予算・人員配置等）に重点を置き、テックリードは技術的な意思決定（アーキテクチャ設計・技術選定・コードレビュー等）に重点を置きます。どちらも上流工程のスキルが必要です。",
    },
    {
      question: "上流工程を学ぶのにマネジメント経験は必要ですか？",
      answer:
        "必須ではありません。ただし、社会人としてのコミュニケーション経験や、チームでの協働経験があると理解が深まります。ゆめさくエンジニアではビジネス経験を活かしながら、技術面の知識を補完する形で学べます。",
    },
  ],
  relatedLanguages: [
    { slug: "system-design", name: "システム設計" },
    { slug: "rest-api", name: "REST API" },
    { slug: "team-dev", name: "チーム開発" },
    { slug: "dx-literacy", name: "DXリテラシー" },
  ],
};
